import isNil from 'lodash/isNil';

import { getLocaleFromLanguage } from './dateutils';
import { Language } from './types';

interface FormatOptions {
  style?: 'decimal' | 'currency' | 'percent';
  currency?: 'EUR' | 'USD' | 'CNY' | 'CHF' | 'CAD' | 'GBP' | 'MXN' | 'NOK';
  maximumSignificantDigits?: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  [key: string]: any;
}

export const PERCENT = 100;
const MAXIMUM_PERCENTAGE_FRACTION_DIGITS = 2;

const NUMBER_FORMATS = new Map<string, Intl.NumberFormat>();
const LIST_FORMATS = new Map<string, Intl.ListFormat>();

const getNumberFormat = (locale: string, options: FormatOptions): Intl.NumberFormat => {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = NUMBER_FORMATS.get(key);
  if (!isNil(cached)) {
    return cached;
  }
  const numberFormat = new Intl.NumberFormat(locale, options);
  NUMBER_FORMATS.set(key, numberFormat);
  return numberFormat;
};

const getListFormat = (locale: string): Intl.ListFormat => {
  const cached = LIST_FORMATS.get(locale);
  if (!isNil(cached)) {
    return cached;
  }
  const listFormat = new Intl.ListFormat(locale, { type: 'unit' });
  LIST_FORMATS.set(locale, listFormat);
  return listFormat;
};

const toLocaleCode = (language: Language): string => getLocaleFromLanguage(language).code ?? language;

export const formatNumber = (number: number, locale: string = 'fr', options: FormatOptions = {}) =>
  getNumberFormat(locale, options).format(number);

export const formatEurRounded = (amount: number, language: Language): string =>
  formatNumber(amount, toLocaleCode(language), {
    currency: 'EUR',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'currency',
  });

export const formatEurCompact = (amount: number, language: Language): string =>
  formatNumber(amount, toLocaleCode(language), {
    currency: 'EUR',
    maximumFractionDigits: 1,
    notation: 'compact',
    style: 'currency',
  });

/** `percentage` is expressed in percent: 2.5 is formatted as +2.5 %. */
export const formatSignedPercentage = (percentage: number, language: Language): string =>
  formatNumber(percentage / PERCENT, toLocaleCode(language), {
    maximumFractionDigits: MAXIMUM_PERCENTAGE_FRACTION_DIGITS,
    signDisplay: 'exceptZero',
    style: 'percent',
  });

export const formatList = (items: string[], language: Language): string =>
  getListFormat(toLocaleCode(language)).format(items);

export const formatNumberToPercent = (number: number, locale: string = 'fr') => {
  return formatNumber(number, locale, { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 2 });
};
export const formatNumberToEuroCurrency = (
  number: number,
  locale?: string | null,
  { signDisplay = 'auto' }: Pick<Intl.NumberFormatOptions, 'signDisplay'> = {}
) => formatNumber(number, locale ?? Language.EN, { style: 'currency', currency: 'EUR', signDisplay });

/** Variation from `previous` to `next`, in percent rounded to the cent; null when there is nothing to compare with. */
export const getVariationPercentage = (previous: number, next: number): number | null =>
  previous === 0 ? null : roundAtSecondDecimal(((next - previous) / previous) * PERCENT);

const AMOUNT_FRACTION_DIGITS = 2;
const GROUPING_DIGITS_COUNT = 3;
const GROUPING_SAMPLE_NUMBER = 12345.6;
const DIGIT = /\d/;
const SIGN = /-/g;
const SEPARATORS = /[.,]/g;
const NOT_PART_OF_A_NUMBER = /[^\d.,-]/g;

export const formatAmountForInput = (amount: number, locale?: string | null) =>
  formatNumber(amount, locale ?? Language.EN, {
    minimumFractionDigits: AMOUNT_FRACTION_DIGITS,
    maximumFractionDigits: AMOUNT_FRACTION_DIGITS,
    useGrouping: false,
  });

const getGroupingSeparator = (locale: string) =>
  new Intl.NumberFormat(locale).formatToParts(GROUPING_SAMPLE_NUMBER).find((part) => part.type === 'group')?.value;

const getDecimalSeparator = (digitsAndSeparators: string, groupingSeparator: string | undefined) => {
  const lastDotIndex = digitsAndSeparators.lastIndexOf('.');
  const lastCommaIndex = digitsAndSeparators.lastIndexOf(',');
  if (lastDotIndex !== -1 && lastCommaIndex !== -1) {
    return lastDotIndex > lastCommaIndex ? '.' : ',';
  }
  if (lastDotIndex === -1 && lastCommaIndex === -1) {
    return null;
  }
  const separator = lastDotIndex !== -1 ? '.' : ',';
  const occurrences = digitsAndSeparators.split(separator).length - 1;
  if (occurrences > 1) {
    return null;
  }
  const digitsAfterSeparator = digitsAndSeparators.length - digitsAndSeparators.lastIndexOf(separator) - 1;
  if (digitsAfterSeparator === GROUPING_DIGITS_COUNT && separator === groupingSeparator) {
    return null;
  }
  return separator;
};

export const parseLocalizedAmount = (rawValue: string, locale?: string | null): number | null => {
  const cleanedValue = rawValue.replace(NOT_PART_OF_A_NUMBER, '');
  if (!DIGIT.test(cleanedValue)) {
    return null;
  }
  const isNegative = cleanedValue.startsWith('-');
  const digitsAndSeparators = cleanedValue.replace(SIGN, '');
  const decimalSeparator = getDecimalSeparator(digitsAndSeparators, getGroupingSeparator(locale ?? Language.EN));
  const canonicalValue = isNil(decimalSeparator)
    ? digitsAndSeparators.replace(SEPARATORS, '')
    : digitsAndSeparators
        .split(decimalSeparator)
        .map((part) => part.replace(SEPARATORS, ''))
        .join('.');
  const parsedValue = Number(canonicalValue);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }
  return isNegative ? -parsedValue : parsedValue;
};

export const toBoundedPercent = (value: number, total: number): number =>
  Math.min(PERCENT, Math.max(0, (value / Math.max(total, 1)) * PERCENT));

export const parseAmountInput = (raw: string): number | null => {
  const normalized = raw.replace(/\s/g, '').replace(',', '.');
  if (normalized === '') {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? roundAtSecondDecimal(parsed) : null;
};

export const roundAtSecondDecimal = (number: number) => {
  const rounded = (Math.sign(number) * Math.round((Math.abs(number) + Number.EPSILON) * 100)) / 100;
  // To avoid returning -0
  if (Math.abs(rounded) === 0) {
    return 0;
  }

  return rounded;
};

const CENTS_PER_UNIT = 100;

export const FLOATING_POINT_TOLERANCE = 0.01;

export const toCents = (amount: number): number => Math.round(amount * CENTS_PER_UNIT);

export const fromCents = (cents: number): number => roundAtSecondDecimal(cents / CENTS_PER_UNIT);

export const roundToDecimalPlace = (number: number, decimalPlaces: number) => {
  const factor = 10 ** decimalPlaces;
  return Math.round((number + Number.EPSILON) * factor) / factor;
};

export const convertAmountToNegativeIfCondition = (amount: number, condition: boolean) =>
  amount !== 0 && condition ? -amount : amount;

export const convertNumberToHexByte = (number: number) => number.toString(16).padStart(2, '0');

export const truncateNumber = (number: number, nbDecimalsToKeep: number) => {
  if (nbDecimalsToKeep < 0) return number;
  const strNumber = number.toString();
  const decimalIndex = strNumber.indexOf('.');
  if (decimalIndex === -1) return number;
  return Number(strNumber.slice(0, decimalIndex + 1 + nbDecimalsToKeep));
};

export const countDecimals = (nb: number) => {
  const str = nb.toString();
  const commaIndex = str.indexOf('.');
  if (commaIndex === -1) return 0;
  return str.length - commaIndex - 1;
};

export const sumByIf = <T>(array: T[], iteratee: keyof T | ((item: T) => number), condition: (item: T) => boolean) => {
  const sum = array.reduce((currentSum, item) => {
    if (condition(item)) {
      const value = typeof iteratee === 'function' ? iteratee(item) : item[iteratee];
      return currentSum + (typeof value === 'number' ? value : 0);
    }
    return currentSum;
  }, 0);
  return roundAtSecondDecimal(sum);
};
